import { useMemo, useEffect, memo, useState, useCallback } from 'react'
import { observer } from 'mobx-react'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { useTheme, useMediaQuery } from '@material-ui/core'
import { withStyles } from '@material-ui/styles'
import {
  OptimizelyExperiment,
  OptimizelyVariation
} from '@optimizely/react-sdk'

import { TEAL_BEST_OFFER_TEST } from 'OptimizelyProvider'
import MultipleInputSkeleton from 'components/SkeletonView/MultipleInputSkeleton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { trackBestOfferView } from 'utils/tracking'
import { formatToDeAmount, formatToDecimal } from 'utils/number'
import { FOOTER_PORTAL_DIV_ID } from 'components/Portal/Footer'
import { Portal } from 'components/Portal'
import { useStores } from 'stores/utils/hooks/useStores'
import { BestOfferRequestState, useBestOffer } from 'hooks/useBestOffer'
import CategoryLayout from 'components/Layout/Category'
import CheckmarkIcon from './CheckmarkIcon.svg'

const BANK_LOGO_HEIGHT = 80

const StyledTypography = withStyles((theme: Theme) =>
  createStyles({
    root: {
      color: theme.palette.grey[600],
      fontSize: 10,
      lineHeight: 1.4
    }
  })
)(Typography)

const useBestOfferStyles = makeStyles<
  Theme,
  { isLogoLoaded: boolean; bestOfferRequestState: BestOfferRequestState }
>((theme: Theme) =>
  createStyles({
    slimVariantContainer: {
      display: 'flex',
      justifyContent: 'space-between',

      [theme.breakpoints.up('sm')]: {
        justifyContent: 'flex-start',
        gap: theme.spacing(2)
      }
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'center',
      textAlign: 'center',
      opacity: ({ isLogoLoaded, bestOfferRequestState }) =>
        !isLogoLoaded ||
        bestOfferRequestState === BestOfferRequestState.Updating
          ? 0.3
          : 1
    },
    bestOfferTop: {
      background: theme.palette.primary.main,
      borderTopLeftRadius: theme.shape.borderRadius * 4,
      borderTopRightRadius: theme.shape.borderRadius * 4,
      color: theme.palette.info.contrastText,
      padding: theme.spacing(1)
    },
    bestOfferBox: {
      border: `1px solid #165297`,
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      display: 'flex',
      justifyContent: 'space-around',
      padding: theme.spacing(2)
    },
    bestOfferInfo: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      textAlign: 'left'
    },
    bestOfferBoxContainer: {
      margin: theme.spacing(2, 0)
    },
    checkmarkIcon: {
      marginBottom: theme.spacing(1),
      alignSelf: 'center'
    },
    bankLogo: {
      objectFit: 'contain',
      height: `${BANK_LOGO_HEIGHT}px`,
      width: 'auto',
      marginBottom: theme.spacing(2)
    },
    bestOfferHomePageStyle: {
      backgroundColor: theme.palette.primary[300],
      borderRadius: '4px',
      padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
      textAlign: 'left'
    },
    bestOfferHomePageStyleDetails: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    bestOfferHomePageStyleLabel: {
      marginRight: `${theme.spacing(1)}px`
    },
    bestOfferHomePageStyleHeader: {
      fontWeight: 'normal',
      marginBottom: `${theme.spacing(1)}px`
    },
    bestOfferHomePageStyleRateLabel: {
      marginRight: `${theme.spacing(1)}px`,
      color: `${theme.palette.grey[900]}`
    }
  })
)

const monthToYearString = (months: number) => {
  const years = months / 12
  const yearSuffixString = years === 1 ? 'Jahr' : 'Jahre'
  return `${years} ${yearSuffixString}`
}

export const BestOfferBoxSkeleton = () => (
  <MultipleInputSkeleton count={3} data-testid="best-offer-skeleton" />
)

interface BestOfferProps {
  displaySurroundingText?: boolean
  displayProductLogo?: boolean
  displayHomePageStyle?: boolean
  useSlimVariant?: boolean
}

const BestOfferBox = observer(
  ({
    displaySurroundingText = true,
    displayProductLogo = false,
    displayHomePageStyle = false,
    useSlimVariant
  }: BestOfferProps) => {
    const theme = useTheme()
    const isLargeLayout = useMediaQuery(theme.breakpoints.up('sm'))

    const { rootStore } = useRootStore()
    const {
      traversal: {
        data: {
          common: { amount, term, purpose }
        }
      }
    } = useStores()
    const { bestOffer, bestOfferRequestState } = useBestOffer()

    const showBestOffer = useMemo(
      () =>
        [
          BestOfferRequestState.Completed,
          BestOfferRequestState.Updating
        ].includes(bestOfferRequestState) && bestOffer,
      [bestOffer, bestOfferRequestState]
    )

    const [isLogoLoaded, setLogoLoaded] = useState(!displayProductLogo)
    const onLogoLoaded = useCallback(() => {
      setLogoLoaded(true)
    }, [])

    useEffect(() => {
      setLogoLoaded(!displayProductLogo)
    }, [bestOffer?.product.images.color.svg, displayProductLogo])

    useEffect(() => {
      if (showBestOffer) {
        trackBestOfferView(rootStore, amount, term, purpose, bestOffer!)
      }
    }, [showBestOffer, amount, term, purpose, bestOffer, rootStore])

    const {
      slimVariantContainer,
      container,
      bestOfferBox,
      bestOfferInfo,
      bestOfferBoxContainer,
      checkmarkIcon,
      bankLogo,
      bestOfferTop,
      bestOfferHomePageStyle,
      bestOfferHomePageStyleDetails,
      bestOfferHomePageStyleLabel,
      bestOfferHomePageStyleHeader,
      bestOfferHomePageStyleRateLabel
    } = useBestOfferStyles({ bestOfferRequestState, isLogoLoaded })

    if (bestOfferRequestState === BestOfferRequestState.Loading) {
      return <BestOfferBoxSkeleton />
    }

    if (!bestOffer) {
      return null
    }

    const installment = bestOffer.monthlyRate || 0
    const interestRate = bestOffer.effectiveInterestRate || 0

    if (useSlimVariant) {
      return (
        <OptimizelyExperiment experiment={TEAL_BEST_OFFER_TEST} timeout={3000}>
          <OptimizelyVariation variation="variation_1">
            <div className={slimVariantContainer}>
              <div>
                <Typography variant="body2">Effekt. Jahreszins</Typography>
                <Typography variant="h5" component="p" color="primary">
                  <b>ab {formatToDecimal(interestRate)}%</b>
                </Typography>
              </div>
              <div>
                <Typography variant="body2">Monatsrate ab</Typography>
                <Typography variant="h5" component="p" color="primary">
                  <b>{formatToDeAmount(installment, 2)}</b>
                </Typography>
              </div>
            </div>
          </OptimizelyVariation>
        </OptimizelyExperiment>
      )
    }

    return (
      <div className={container}>
        {showBestOffer ? (
          <>
            {displayProductLogo && (
              <img
                className={bankLogo}
                alt={`logo-${bestOffer!.product.bank.name}`}
                src={bestOffer!.product.images.color.svg}
                height={BANK_LOGO_HEIGHT}
                onLoad={onLogoLoaded}
                data-testid="best-offer-product-logo"
              />
            )}
            {displaySurroundingText && (
              <>
                <Typography
                  variant="h6"
                  component="p"
                  gutterBottom
                  data-testid="best-offer-surrounding-text"
                >
                  Vielen Dank,
                </Typography>
                <Typography variant="body2">
                  für Ihr Vertrauen. Das bestmögliche Angebot für Ihren
                  Wunschbetrag ist Folgendes:
                </Typography>
              </>
            )}
            <div className={bestOfferBoxContainer} data-testid="best-offer-box">
              {displayHomePageStyle ? (
                <div className={bestOfferHomePageStyle}>
                  <Typography
                    variant="h5"
                    className={bestOfferHomePageStyleHeader}
                  >
                    Günstigster Kredit aus über 60 Angeboten ab:
                  </Typography>
                  <div className={bestOfferHomePageStyleDetails}>
                    <div>
                      <Typography
                        variant="h2"
                        component="span"
                        className={bestOfferHomePageStyleRateLabel}
                      >
                        {formatToDeAmount(installment, 2)}
                      </Typography>
                      <Typography variant="body1" component="span">
                        Mtl. Rate
                      </Typography>
                    </div>
                    <Typography variant="h2">
                      <Typography
                        variant="h2"
                        component="span"
                        className={bestOfferHomePageStyleLabel}
                      >
                        {formatToDecimal(interestRate)}%
                      </Typography>
                      <Typography variant="body1" component="span">
                        Eff. Jahreszins ab*
                      </Typography>
                    </Typography>
                  </div>
                </div>
              ) : (
                <>
                  <div className={bestOfferTop}>
                    <Typography variant="body2" color="inherit">
                      Kreditbetrag: <b>{formatToDeAmount(amount)}</b> |
                      Laufzeit: <b>{monthToYearString(term)}</b>
                    </Typography>
                  </div>
                  <div className={bestOfferBox}>
                    <div className={bestOfferInfo}>
                      <Typography variant={isLargeLayout ? 'body2' : 'caption'}>
                        Monatl. Rate ab
                      </Typography>
                      <Typography
                        variant={isLargeLayout ? 'h5' : 'h6'}
                        component="p"
                        color="primary"
                      >
                        <b>{formatToDeAmount(installment, 2)}</b>
                      </Typography>
                    </div>

                    <div className={bestOfferInfo}>
                      <Typography variant={isLargeLayout ? 'body2' : 'caption'}>
                        Eff. Jahreszins ab*
                      </Typography>
                      <Typography
                        variant={isLargeLayout ? 'h5' : 'h6'}
                        component="p"
                        color="primary"
                      >
                        <b>{formatToDecimal(interestRate)}%</b>
                      </Typography>
                    </div>
                  </div>
                </>
              )}
            </div>
            {displaySurroundingText && (
              <Typography variant="body2">
                In den nächsten Schritten geht es um die Erstellung Ihrer
                individuellen Kreditangebote. Hierfür benötigen wir noch weitere
                Angaben von Ihnen.
              </Typography>
            )}
          </>
        ) : (
          <>
            <img
              alt="checkmark"
              src={CheckmarkIcon}
              className={checkmarkIcon}
            />
            <Typography variant="h6" component="p" gutterBottom>
              Sehr gut,
            </Typography>
            <Typography variant="body2" gutterBottom>
              der erste Schritt ist schon geschafft!
            </Typography>

            <Typography variant="body2">
              Um die für Sie besten Kreditangebote aus unserem Kreditvergleich
              zu ermitteln, brauchen wir nun noch weitere für die
              Zinskonditionen relevante Angaben von Ihnen.
            </Typography>
          </>
        )}
        {!displayHomePageStyle && (
          <Portal targetDomID={FOOTER_PORTAL_DIV_ID}>
            <CategoryLayout>
              <StyledTypography>* {bestOffer.legalText}</StyledTypography>
            </CategoryLayout>
          </Portal>
        )}
      </div>
    )
  }
)

export default memo(BestOfferBox)
